// reportSync.ts
import { authApi, reportsApi } from '../api/authApi';
// TODO: Need financial reports API for database operations
import { AccountEntry, CategoryTemplate } from '../types';
import { compareMonthYear, getPeriodDays } from './calculations';
import { calculateNWCMetrics } from './nwcMetrics';
import { buildNWCDetailReport } from './reportBuilder';
import { buildPLDetailReport, buildBSDetailReport, buildCFDetailReport } from './reportBuilder';
import { buildFinancialRatiosReport } from './reportBuilder';

// Utility to get all unique months from accounts, sorted
function getAllMonths(accounts: AccountEntry[]): string[] {
  const months = [...new Set(accounts.map(a => a.month))];
  months.sort((a, b) => {
    const [ma, ya] = a.split('-');
    const [mb, yb] = b.split('-');
    const monthMap: Record<string, number> = {
      Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
      Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
    };
    return Number(ya) !== Number(yb)
      ? Number(ya) - Number(yb)
      : monthMap[ma] - monthMap[mb];
  });
  return months;
}

// Helper to calculate category totals for a specific month
function calculateCategoryTotal(
  accounts: AccountEntry[],
  category: string,
  month: string,
  isRevenue: boolean = false
): number {
  const categoryAccounts = accounts.filter(a => a.category === category && a.month === month);
  
  return categoryAccounts.reduce((total, account) => {
    if (isRevenue) {
      // For revenue accounts, credit balance should be positive
      return total + (account.credit - account.debit);
    } else {
      // For expense/asset accounts, debit balance should be positive
      return total + (account.debit - account.credit);
    }
  }, 0);
}

// Helper to build time series data for line items
function buildTimeSeriesData(
  accounts: AccountEntry[],
  activeTemplate: CategoryTemplate,
  months: string[],
  periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'
): Record<string, Record<string, any>> {
  const reports: Record<string, Record<string, any>> = {
    pnl_detail: {},
    pnl_percent: {},
    bs_detail: {},
    cf_detail: {},
    nwc_detail: {}
  };

  // --- P&L Detail (refactored) ---
  reports.pnl_detail = buildPLDetailReport(accounts, activeTemplate, months, periodType);

  // Extract Net Revenue time series from P&L detail
  const netRevenueItem = reports.pnl_detail.line_items.find((item: any) => item.name === 'Net Revenue');
  const netRevenueTimeSeries: Record<string, number> = {};
  if (netRevenueItem && Array.isArray(netRevenueItem.values)) {
    months.forEach((month, idx) => {
      netRevenueTimeSeries[month] = netRevenueItem.values[idx] || 0;
    });
  }

  // --- NWC Detail ---
  // Use period-aware view type for sync
  const nwcViewType = periodType === 'annual' ? 'yearly' : periodType === 'quarterly' ? 'quarterly' : 'monthly';
  reports.nwc_detail = buildNWCDetailReport(accounts, activeTemplate, months, netRevenueTimeSeries, nwcViewType, periodType);

  // --- The rest of the report types remain as before ---

  // Build P&L Percent data (as % of revenue)
  console.log('[ReportSync] Building P&L Percent data...');
  const netRevenueValues: number[] = Array.isArray(netRevenueItem?.values) ? netRevenueItem.values as number[] : [];
  const plPercentData: { periods: string[]; line_items: any[] } = {
    periods: getAllMonths(accounts),
    line_items: [] as any[]
  };
  reports.pnl_detail.line_items.forEach((item: any) => {
    // Include section_header items without values conversion
    if (item.type === 'section_header') {
      plPercentData.line_items.push({
        ...item
        // section_header items don't have values, so keep them as-is
      });
      return;
    }
    
    // Only process items with values array for percentage conversion
    if (!Array.isArray(item.values)) return;
    const percentValues = item.values.map((val: number, idx: number) => {
      const netRev = netRevenueValues[idx] || 0;
      if (!netRev || netRev === 0 || val === null || val === undefined) return null;
      return Math.round((val / netRev) * 1000) / 10; // 1 decimal place
    });
    plPercentData.line_items.push({
      ...item,
      values: percentValues,
      type: item.type === 'calculated_total' ? 'calculated_percentage' : item.type // Mark as percentage if total
    });
  });
  reports.pnl_percent = plPercentData;

  // Build Balance Sheet Detail data (array-based structure)
  console.log('[ReportSync] Building Balance Sheet Detail (array-based) data...');

  const bsSections = [
    { id: 'current-assets', title: 'Current Assets', types: ['current-asset', 'contra-current-asset'] },
    { id: 'non-current-assets', title: 'Non-current Assets', types: ['non-current-asset', 'contra-non-current-asset'] },
    { id: 'current-liabilities', title: 'Current Liabilities', types: ['current-liability', 'contra-current-liability'] },
    { id: 'non-current-liabilities', title: 'Non-current Liabilities', types: ['non-current-liability', 'contra-non-current-liability'] },
    { id: 'equity', title: 'Equity', types: ['equity', 'contra-equity'] }
  ];

  const getCategoriesByType = (types: string[]) =>
    activeTemplate.categories.filter(cat => types.includes(cat.type)).map(cat => cat.value);
  const getCategoryDisplayName = (categoryValue: string) =>
    activeTemplate.categories.find(cat => cat.value === categoryValue)?.label || categoryValue;
  const getCategoryType = (category: string) =>
    activeTemplate.categories.find(cat => cat.value === category)?.type || 'n/a';
  const getCategoryAccountsByMonth = (category: string, month: string) =>
    accounts.filter(account => account.category === category && account.month === month);
  const calculateMonthlyTotal = (category: string, month: string) => {
    const monthlyAccounts = getCategoryAccountsByMonth(category, month);
    const categoryType = getCategoryType(category);
    const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
    return monthlyAccounts.reduce((total, account) => {
      return total + ((isAsset || isContraAsset)
        ? account.debit - account.credit
        : account.credit - account.debit);
    }, 0);
  };
  const calculateSectionTotal = (types: string[], month: string) => {
    const categories = getCategoriesByType(types);
    return categories.reduce((total, category) => total + calculateMonthlyTotal(category, month), 0);
  };
  const calculateTotalAssets = (month: string) =>
    calculateSectionTotal(['current-asset', 'contra-current-asset', 'non-current-asset', 'contra-non-current-asset'], month);
  const calculateTotalLiabilities = (month: string) =>
    calculateSectionTotal(['current-liability', 'contra-current-liability', 'non-current-liability', 'contra-non-current-liability'], month);
  const getMonthAccounts = (month: string) => accounts.filter(account => account.month === month);
  const calculateNetIncome = (month: string) => {
    const monthAccounts = getMonthAccounts(month);
    const revenueCategories = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value);
    const contraRevenueCategories = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value);
    const expenseCategories = activeTemplate.categories.filter(cat => ['cogs', 'contra-cogs', 'operating-expense', 'interest-expense', 'income-tax-expense', 'other-expense'].includes(cat.type)).map(cat => cat.value);
    const revenue = monthAccounts.filter(account => account.category && revenueCategories.includes(account.category as string)).reduce((total, account) => total + (account.credit - account.debit), 0);
    const contraRevenue = monthAccounts.filter(account => account.category && contraRevenueCategories.includes(account.category as string)).reduce((total, account) => total + (account.debit - account.credit), 0);
    const expenses = monthAccounts.filter(account => account.category && expenseCategories.includes(account.category as string)).reduce((total, account) => total + (account.debit - account.credit), 0);
    return revenue - contraRevenue - expenses;
  };
  const calculateTotalEquity = (month: string) =>
    calculateSectionTotal(['equity', 'contra-equity'], month) + calculateNetIncome(month);
  const calculateLiabilitiesAndEquityTotal = (month: string) =>
    calculateTotalLiabilities(month) + calculateTotalEquity(month);
  const calculateCheck = (month: string) => {
    const totalAssets = calculateTotalAssets(month);
    const totalLiabilitiesAndEquity = calculateLiabilitiesAndEquityTotal(month);
    return totalAssets - totalLiabilitiesAndEquity;
  };
  const getAccountDisplayOrder = (categoryValue: string) => {
    const categoryAccounts = accounts.filter(acc => acc.category === categoryValue);
    const uniqueAccountNumbers = [...new Set(categoryAccounts.map(acc => acc.accountNumber))].filter(acc => acc !== undefined).sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
    return uniqueAccountNumbers;
  };

  const sortedMonths = getAllMonths(accounts);
  const bsDetailData = {
    periods: sortedMonths,
    line_items: [] as any[]
  };

  bsSections.forEach((section, sectionIndex) => {
    const categories = getCategoriesByType(section.types);
    console.log(`[BS Sync] Processing section: ${section.title}`);
    bsDetailData.line_items.push({
      type: 'section_header',
      name: section.title,
      section_id: section.id,
      level: 1
    });
    categories.forEach(categoryValue => {
      const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
      if (!category) return;
      const hasAccounts = sortedMonths.some(month => getCategoryAccountsByMonth(categoryValue ?? '', month).length > 0);
      if (!hasAccounts) return;
      console.log(`[BS Sync]   Category: ${getCategoryDisplayName(categoryValue ?? '')}`);
      bsDetailData.line_items.push({
        type: 'category_header',
        name: getCategoryDisplayName(categoryValue ?? ''),
        category_value: categoryValue,
        level: 2
      });
      const orderedAccountNumbers = getAccountDisplayOrder(categoryValue ?? '');
      orderedAccountNumbers.forEach(accountNumber => {
        const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
        const monthlyValues = sortedMonths.map(month => {
          const monthlyAccount = accounts.find(a => a.accountNumber === accountNumber && a.category === categoryValue && a.month === month);
          if (!monthlyAccount) return 0;
          const categoryType = getCategoryType(categoryValue);
          const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
          const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
          return (isAsset || isContraAsset)
            ? monthlyAccount.debit - monthlyAccount.credit
            : monthlyAccount.credit - monthlyAccount.debit;
        });
        if (monthlyValues.some(val => val !== 0)) {
          console.log(`[BS Sync]     Account: ${accountName} (${accountNumber})`);
          bsDetailData.line_items.push({
            type: 'account',
            name: accountName,
            account_number: accountNumber,
            category: categoryValue,
            values: monthlyValues,
            level: 3
          });
        }
      });
      const categoryTotalValues = sortedMonths.map(month => calculateMonthlyTotal(categoryValue, month));
      if (categoryTotalValues.some(val => val !== 0)) {
        console.log(`[BS Sync]   Category Total: ${getCategoryDisplayName(categoryValue ?? '')}`);
        bsDetailData.line_items.push({
          type: 'category_total',
          name: `Total ${getCategoryDisplayName(categoryValue ?? '')}`,
          category: categoryValue,
          values: categoryTotalValues,
          level: 2
        });
      }
    });
    if (section.id === 'equity') {
      const netIncomeValues = sortedMonths.map(month => calculateNetIncome(month));
      bsDetailData.line_items.push({
        type: 'calculated_total',
        name: 'Current Period Net Income',
        values: netIncomeValues,
        level: 2
      });
    }
    const sectionTotalValues = sortedMonths.map(month => {
      return section.id === 'equity'
        ? calculateSectionTotal(section.types, month) + calculateNetIncome(month)
        : calculateSectionTotal(section.types, month);
    });
    if (sectionTotalValues.some(val => val !== 0)) {
      console.log(`[BS Sync] Section Total: ${section.title}`);
      bsDetailData.line_items.push({
        type: 'section_total',
        name: `Total ${section.title}`,
        section_id: section.id,
        values: sectionTotalValues,
        level: 1
      });
    }
    if (sectionIndex === 1) {
      const totalAssetsValues = sortedMonths.map(month => calculateTotalAssets(month));
      bsDetailData.line_items.push({
        type: 'major_total',
        name: 'Total Assets',
        values: totalAssetsValues,
        level: 0
      });
    }
    if (sectionIndex === 3) {
      const totalLiabilitiesValues = sortedMonths.map(month => calculateTotalLiabilities(month));
      bsDetailData.line_items.push({
        type: 'major_total',
        name: 'Total Liabilities',
        values: totalLiabilitiesValues,
        level: 0
      });
    }
  });
  const totalLiabilitiesAndEquityValues = sortedMonths.map(month => calculateLiabilitiesAndEquityTotal(month));
  bsDetailData.line_items.push({
    type: 'major_total',
    name: 'Total Liabilities and Equity',
    values: totalLiabilitiesAndEquityValues,
    level: 0
  });
  const checkValues = sortedMonths.map(month => calculateCheck(month));
  bsDetailData.line_items.push({
    type: 'balance_check',
    name: 'Check (Assets - L&E)',
    values: checkValues,
    level: 0,
    is_check: true
  });
  console.log('[BS Sync] Created', bsDetailData.line_items.length, 'line items');
  console.log('[BS Sync] Periods:', bsDetailData.periods);
  reports.bs_detail = bsDetailData;

  // Build Cash Flow Detail data (hierarchical, ordered structure)
  console.log('[ReportSync] Building Cash Flow Detail (hierarchical) data...');
  reports.cf_detail = buildCFDetailReport(accounts, activeTemplate, months);

  return reports;
}

// Check if financial_reports table exists and create if needed
async function ensureFinancialReportsTable() {
  try {
    // TODO: Replace with financial reports API call
    // For now, assume table exists
    console.log('[ReportSync] Skipping table check - needs financial reports API');
    return true;
  } catch (error) {
    console.error('[ReportSync] Error checking financial_reports table:', error);
    throw error;
  }
}

// Helper function to deduplicate accounts by accountNumber + month
function deduplicateAccounts(accounts: AccountEntry[]): AccountEntry[] {
  const accountMap = new Map<string, AccountEntry>();
  
  accounts.forEach(account => {
    const key = `${account.accountNumber}-${account.month}`;
    if (!accountMap.has(key)) {
      accountMap.set(key, account);
    }
  });
  
  return Array.from(accountMap.values());
}

// Main sync function
export async function syncAllFinancialReportsToSupabase({
  projectId,
  accounts,
  activeTemplate,
  periodType = 'monthly',
}: {
  projectId: string;
  accounts: AccountEntry[];
  activeTemplate: CategoryTemplate;
  periodType?: 'monthly' | 'quarterly' | 'annual';
}) {
  try {
    // Check if user is authenticated
    const sessionValidation = await authApi.validateSession();
    if (!sessionValidation.valid) {
      console.error('No authenticated user found. Aborting report sync.');
      return;
    }
    
    console.log('[ReportSync] Authenticated user, Project:', projectId);

    if (!projectId || !accounts.length || !activeTemplate) {
      console.warn('syncAllFinancialReportsToSupabase: Missing required data', { 
        projectId, 
        accountsLength: accounts.length, 
        hasTemplate: !!activeTemplate 
      });
      return;
    }

    // Debug: Log raw account data before any processing
    console.log('[ReportSync DEBUG] Raw account data analysis:', {
      totalAccounts: accounts.length,
      periodType: periodType,
      periodTypeSource: 'Parameter passed to syncAllFinancialReportsToSupabase',
      sampleAccounts: accounts.slice(0, 3).map(acc => ({
        accountNumber: acc.accountNumber,
        name: acc.name,
        month: acc.month,
        debit: acc.debit,
        credit: acc.credit,
        category: acc.category
      }))
    });

    // DEBUG: Check if the months suggest annual data despite periodType being monthly
    const monthsAnalysis = {
      uniqueMonths: [...new Set(accounts.map(acc => acc.month))],
      monthPattern: accounts.slice(0, 5).map(acc => acc.month),
      looksLikeAnnual: accounts.some(acc => acc.month && acc.month.includes('Dec-'))
    };
    console.log('[ReportSync DEBUG] Months analysis:', monthsAnalysis);

    // Check for potential duplicates in raw data
    const accountKeys = accounts.map(acc => `${acc.accountNumber}-${acc.month}`);
    const uniqueKeys = new Set(accountKeys);
    console.log('[ReportSync DEBUG] Duplicate analysis:', {
      totalKeys: accountKeys.length,
      uniqueKeys: uniqueKeys.size,
      hasDuplicates: accountKeys.length !== uniqueKeys.size
    });

    // Deduplicate accounts to prevent double-counting
    const deduplicatedAccounts = deduplicateAccounts(accounts);
    console.log('[ReportSync] Deduplicated accounts:', {
      original: accounts.length,
      deduplicated: deduplicatedAccounts.length,
      duplicatesRemoved: accounts.length - deduplicatedAccounts.length
    });

    // Check if financial_reports table exists
    try {
      await ensureFinancialReportsTable();
    } catch (error) {
      console.error('[ReportSync] Skipping sync due to table access error:', error);
      return;
    }

    const months = getAllMonths(deduplicatedAccounts);
    console.log('[ReportSync] Processing months:', months);

    console.log('[DEBUG] syncAllFinancialReportsToSupabase triggered for project:', projectId);
    // Build all time series data
    const reportsData = buildTimeSeriesData(deduplicatedAccounts, activeTemplate, months, periodType);
    
    // Debug: Log what gets generated for annual P&L data
    if (periodType === 'annual' && reportsData.pnl_detail) {
      const revenueItems = reportsData.pnl_detail.line_items?.filter(item => 
        item.name?.toLowerCase().includes('revenue') || 
        item.name?.toLowerCase().includes('sales')
      ) || [];
      
      console.log('[ReportSync DEBUG] Generated P&L data for ANNUAL:', {
        periodsInReport: reportsData.pnl_detail.periods,
        totalLineItems: reportsData.pnl_detail.line_items?.length || 0,
        revenueItems: revenueItems.map(item => ({
          name: item.name,
          values: item.values,
          type: item.type
        }))
      });
    }

    // Build financial ratios data
    let finRatiosData = null;
    try {
      const ratiosViewType = periodType === 'annual' ? 'yearly' : periodType === 'quarterly' ? 'quarterly' : 'monthly';
      finRatiosData = buildFinancialRatiosReport(
        deduplicatedAccounts,
        activeTemplate,
        {
          pnl_detail: reportsData.pnl_detail,
          bs_detail: reportsData.bs_detail,
          cf_detail: reportsData.cf_detail,
          nwc_detail: reportsData.nwc_detail
        },
        months,
        ratiosViewType,
        'LMA'
      );
    } catch (e: unknown) {
      console.error('[ReportSync] Error calculating financial ratios:');
      if (e instanceof Error) {
        console.error(e);
      } else {
        console.error(String(e));
      }
      finRatiosData = null;
    }

    // Build NWC detail report using new backend logic
    const nwcViewType = periodType === 'annual' ? 'yearly' : periodType === 'quarterly' ? 'quarterly' : 'monthly';
    const nwcDetailData = buildNWCDetailReport(deduplicatedAccounts, activeTemplate, months, undefined, nwcViewType, periodType);

    // Helper to upsert a report with the new structure
    async function upsertReport(report_type: string, data: Record<string, any>) {
      try {
        if (report_type === 'fin_ratios') {
          console.log('[DEBUG] Upserting fin_ratios report:', data);
        }
        console.log(`[ReportSync] Upserting ${report_type}...`);
        
        // Add specific debugging for NWC detail report
        if (report_type === 'nwc_detail') {
          // Debug: Check periodType value
          console.log('[NWC DEBUG] periodType value:', periodType);
          
          // Calculate days used for each period to verify period-aware calculations
          const periodDaysInfo = data.periods?.map((period: string) => {
            // Test the conversion logic manually
            let convertedPeriod = period;
            if (periodType === 'quarterly') {
              const [monthStr, yearStr] = period.split('-');
              const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
              const quarterNum = Math.floor(monthIndex / 3) + 1;
              convertedPeriod = `Q${quarterNum}'${yearStr}`;
            }
            
            const periodDays = getPeriodDays(convertedPeriod, periodType);
            console.log(`[NWC DEBUG] Period: ${period} -> ${convertedPeriod}, periodType: ${periodType}, days: ${periodDays}`);
            return {
              period,
              days: periodDays
            };
          }) || [];

          console.log('[NWC DEBUG] NWC Detail Report Data:', {
            periods: data.periods,
            totalLineItems: data.line_items?.length || 0,
            periodDaysInfo: periodDaysInfo,
            sampleLineItems: data.line_items?.slice(0, 3).map((item: any) => ({
              type: item.type,
              name: item.name,
              values: item.values?.slice(0, 3), // First 3 values
              level: item.level
            })),
            periodType: periodType,
            viewType: nwcViewType
          });
        }
        
        // Use financial reports API to sync to database
        await reportsApi.upsert(projectId, report_type, data, activeTemplate.id);
        console.log(`[ReportSync] Successfully synced ${report_type} to database`);
        if (report_type === 'fin_ratios') {
          console.log('[DEBUG] fin_ratios upsert successful');
        }
      } catch (err) {
        console.error(`[ERROR] Failed to upsert ${report_type}:`, err);
        if (report_type === 'fin_ratios') {
          console.error('[DEBUG] fin_ratios upsert fatal error:', err);
        }
        // Don't throw - continue with other reports
      }
    }

    // Upsert all reports
    const reportTypes = ['pnl_detail', 'pnl_percent', 'bs_detail', 'cf_detail', 'nwc_detail', 'fin_ratios'];
    const reportDataMap = {
      pnl_detail: reportsData.pnl_detail,
      pnl_percent: reportsData.pnl_percent,
      bs_detail: reportsData.bs_detail,
      cf_detail: reportsData.cf_detail,
      nwc_detail: nwcDetailData,
      fin_ratios: finRatiosData
    } as any;

    for (const reportType of reportTypes) {
      await upsertReport(reportType, reportDataMap[reportType] as Record<string, any>);
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    console.log('[ReportSync] ✓ Finished syncing all reports for project', projectId);
  } catch (error) {
    console.error('[ReportSync] Fatal error during sync:', error as any);
    // Don't throw to prevent breaking the main app flow
  }
}
